import { devError } from '../utils/devLogger';
import React, { useState } from 'react';
import { Navbar, Nav, NavDropdown, Badge } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { auth } from '../firebase';
import '../styles/brand.css';

const AdminNavbar = ({ currentPage = 'dashboard' }) => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);

  const handleLogout = async () => {
    try {
      await auth.signOut();
      localStorage.removeItem('adminViewMode');
      navigate('/login');
    } catch (error) {
      devError('Failed to log out', error);
    }
  };

  const handleNavigate = (path) => {
    navigate(path);
    setExpanded(false);
  };

  const handleSwitchToFacilitator = () => {
    localStorage.setItem('adminViewMode', 'facilitator');
    navigate('/facilitator');
    setExpanded(false);
  };

  const isActivePage = (page, path) => currentPage === page || (path && location.pathname === path);

  return (
    <Navbar 
      bg="white"
      variant="light"
      expand="lg" 
      sticky="top"
      expanded={expanded}
      onToggle={setExpanded}
      className="app-navbar"
    >
      <div className="container">
        <Navbar.Brand
          onClick={() => handleNavigate('/admin')}
          style={{ cursor: 'pointer' }}
          className="d-flex align-items-center"
        >
          <div className="d-flex align-items-center gap-2">
            <img
              src="/Access70Logo.png"
              alt="Access70"
              height="40"
              style={{ maxHeight: '40px', width: 'auto' }}
            />
            <img
              src="/NPI_Logo.png"
              alt="NPI Governance Solutions"
              height="50"
              style={{ maxHeight: '50px', width: 'auto' }}
            />
          </div>
        </Navbar.Brand>
        
        <Navbar.Toggle aria-controls="admin-navbar-nav" />
        
        <Navbar.Collapse id="admin-navbar-nav">
          <Nav className="me-auto">
            <NavDropdown 
              title="Dashboard" 
              id="dashboard-dropdown"
              className={isActivePage('dashboard', '/admin') ? 'fw-bold' : ''}
            >
              <NavDropdown.Item 
                onClick={() => handleNavigate('/admin')}
                active={isActivePage('dashboard', '/admin')}
              >
                Overview
              </NavDropdown.Item>
              <NavDropdown.Item 
                onClick={() => handleNavigate('/admin/analytics')}
              >
                System Analytics
              </NavDropdown.Item>
            </NavDropdown>

            <NavDropdown 
              title="User Management" 
              id="users-dropdown"
              className={isActivePage('users') ? 'fw-bold' : ''}
            >
              <NavDropdown.Item 
                onClick={() => handleNavigate('/admin/users')}
                active={isActivePage('users')}
              >
                All Users
              </NavDropdown.Item>
              <NavDropdown.Item 
                onClick={() => handleNavigate('/admin/users?filter=learners')}
              >
                Learners
              </NavDropdown.Item>
              <NavDropdown.Item
                onClick={() => handleNavigate('/admin/users?filter=facilitators')}
              >
                Facilitators
              </NavDropdown.Item>
              <NavDropdown.Divider />
              <NavDropdown.Item
                onClick={() => handleNavigate('/admin/document-upload')}
                active={isActivePage('document-upload')}
              >
                Document Upload
              </NavDropdown.Item>
              <NavDropdown.Divider />
              <NavDropdown.Item 
                onClick={() => handleNavigate('/admin/cohorts')}
              >
                Cohort Management
              </NavDropdown.Item>
              <NavDropdown.Item 
                onClick={() => handleNavigate('/admin/programs')}
              >
                Program Management
              </NavDropdown.Item>
            </NavDropdown>

            <NavDropdown
              title="Composer"
              id="composer-dropdown"
              className={(isActivePage('email-composer') || isActivePage('whatsapp-composer')) ? 'fw-bold' : ''}
            >
              <NavDropdown.Item
                onClick={() => handleNavigate('/email-composer')}
                active={isActivePage('email-composer')}
              >
                Email Composer
              </NavDropdown.Item>
              <NavDropdown.Item
                onClick={() => handleNavigate('/whatsapp-composer')}
                active={isActivePage('whatsapp-composer')}
              >
                WhatsApp Composer
              </NavDropdown.Item>
            </NavDropdown>

            <NavDropdown 
              title="Support" 
              id="support-dropdown"
              className={isActivePage('issues') ? 'fw-bold' : ''}
            >
              <NavDropdown.Item 
                onClick={() => handleNavigate('/admin/issues')}
                active={isActivePage('issues')}
              >
                Issue Tracker
              </NavDropdown.Item>
              <NavDropdown.Item 
                onClick={() => handleNavigate('/admin/messages')}
              >
                Messages
              </NavDropdown.Item>
              <NavDropdown.Item 
                onClick={() => handleNavigate('/admin/notifications')}
              >
                Notifications
              </NavDropdown.Item>
            </NavDropdown>

            <NavDropdown 
              title="Academic" 
              id="academic-dropdown"
              className={isActivePage('attendance') ? 'fw-bold' : ''}
            >
              <NavDropdown.Item 
                onClick={() => handleNavigate('/admin/attendance')}
                active={isActivePage('attendance')}
              >
                Attendance Register
              </NavDropdown.Item>
              <NavDropdown.Item 
                onClick={() => handleNavigate('/admin/progress-reports')}
              >
                Progress Reports
              </NavDropdown.Item>
              <NavDropdown.Item 
                onClick={() => handleNavigate('/admin/assessments')}
              >
                Assessments
              </NavDropdown.Item>
            </NavDropdown>

            <NavDropdown 
              title="Reports" 
              id="reports-dropdown"
              className={isActivePage('reports') ? 'fw-bold' : ''}
            >
              <NavDropdown.Item 
                onClick={() => handleNavigate('/admin/reports')}
                active={isActivePage('reports')}
              >
                Analytics Dashboard
              </NavDropdown.Item>
              <NavDropdown.Item 
                onClick={() => handleNavigate('/admin/reports/attendance')}
              >
                Attendance Reports
              </NavDropdown.Item>
              <NavDropdown.Item 
                onClick={() => handleNavigate('/admin/reports/compliance')}
              >
                Compliance Reports
              </NavDropdown.Item>
              <NavDropdown.Item 
                onClick={() => handleNavigate('/admin/reports/bee')}
              >
                BEE Export
              </NavDropdown.Item>
              <NavDropdown.Item
                onClick={() => handleNavigate('/qa')}
              >
                QA Reports (Daily/Weekly)
              </NavDropdown.Item>
            </NavDropdown>
          </Nav>

          <Nav>
            <NavDropdown
              title={
                <span>
                  <span className="d-none d-lg-inline">Welcome, </span>
                  {userProfile?.displayName || currentUser?.email}
                  <Badge bg="danger" className="ms-2" style={{ fontSize: '0.65rem' }}>Admin</Badge>
                </span>
              }
              id="user-dropdown"
              align="end"
            >
              <NavDropdown.Item onClick={() => handleNavigate('/admin/profile')}>
                My Profile
              </NavDropdown.Item>
              <NavDropdown.Item onClick={() => handleNavigate('/admin/settings')}>
                Settings
              </NavDropdown.Item>
              <NavDropdown.Divider />
              <NavDropdown.Item onClick={handleSwitchToFacilitator} className="text-primary">
                Switch to Facilitator View
              </NavDropdown.Item>
              <NavDropdown.Divider />
              <NavDropdown.Item onClick={handleLogout} className="text-danger">
                Logout
              </NavDropdown.Item>
            </NavDropdown>
          </Nav>
        </Navbar.Collapse>
      </div>
    </Navbar>
  );
};

export default AdminNavbar;
